import { getNamespacedResource } from "SharedStores/util";
import { hourToLocalizedTimeString, monthAndDayToLocalizedDateString } from "Util/clientDateTime";
import { TRIGGER_TYPE, TRIGGER_ACTION_TYPE, ACTION_TYPE } from "Types/automationTypes";
import { DATA_FIELD_TYPE } from "Types/dataFieldTypes";
import { NAMESPACE, KEYS } from "./i18n/programDataStore.i18n";

const PROGRAM_DATA_NAMESPACE = "automationProgramData";

//getters
const GET_TRIGGER_SUMMARY = "getTriggerSummary";
const GET_TRIGGER_TYPE_NAME = "getTriggerTypeName";
const GET_ACTION_SUMMARY = "getActionSummary";
const TRIGGER_SUMMARY_MESSAGE_INFO = "triggerSummaryMessageInfo";
const FORMULAS_BY_ID = "formulasById";
const DATA_FIELDS_BY_ID = "dataFieldsById";
const GET_FORMULAS_BY_RESULT_TYPE = "getFormulasByResultType";

const PROGRAM_DATA_TYPES = {
    getters: {
        [GET_TRIGGER_SUMMARY]: getNamespacedResource(PROGRAM_DATA_NAMESPACE, GET_TRIGGER_SUMMARY),
        [GET_TRIGGER_TYPE_NAME]: getNamespacedResource(PROGRAM_DATA_NAMESPACE, GET_TRIGGER_TYPE_NAME),
        [GET_ACTION_SUMMARY]: getNamespacedResource(PROGRAM_DATA_NAMESPACE, GET_ACTION_SUMMARY),
        [TRIGGER_SUMMARY_MESSAGE_INFO]: getNamespacedResource(PROGRAM_DATA_NAMESPACE, TRIGGER_SUMMARY_MESSAGE_INFO),
        [FORMULAS_BY_ID]: getNamespacedResource(PROGRAM_DATA_NAMESPACE, FORMULAS_BY_ID),
        [DATA_FIELDS_BY_ID]: getNamespacedResource(PROGRAM_DATA_NAMESPACE, DATA_FIELDS_BY_ID),
        [GET_FORMULAS_BY_RESULT_TYPE]: getNamespacedResource(PROGRAM_DATA_NAMESPACE, GET_FORMULAS_BY_RESULT_TYPE)
    }
};

export { PROGRAM_DATA_NAMESPACE, PROGRAM_DATA_TYPES };

export function createAndRegisterProgramDataModule({
    $app,
    $store,
    ProgramData,
    TriggerSummaryMessageInfo,
    ActionSummaryMessageInfo
}) {
    $app.i18n.mergeLocaleMessage($app.i18n.locale, TriggerSummaryMessageInfo.MessagesByActionType);
    $app.i18n.mergeLocaleMessage($app.i18n.locale, ActionSummaryMessageInfo.MessagesByActionType);
    const module = createProgramDataModule({ $app, ProgramData, TriggerSummaryMessageInfo });
    $store.registerModule(PROGRAM_DATA_NAMESPACE, module);
}

function createProgramDataModule({ ProgramData, TriggerSummaryMessageInfo, $app }) {
    function collectionById(collection) {
        const byId = {};
        collection.forEach((item) => {
            byId[item.PublicId] = item;
        });
        return byId;
    }

    /**
     * This method mirrors the logic in TriggerSummary.cs GetTimeArgumentsFromTrigger
     */
    function getTimeArgumentsFromTrigger(trigger) {
        const time = hourToLocalizedTimeString({ hour: trigger.ScheduledHour, locale: $app.i18n.locale });
        const dayOfYear = monthAndDayToLocalizedDateString({
            month: trigger.ScheduledMonth,
            day: trigger.ScheduledDay,
            locale: $app.i18n.locale
        });

        const dayOfMonth = trigger.ScheduledDay?.toString() ?? "";

        const daysOfWeekArr = [];
        if (trigger.ScheduledSunday) {
            daysOfWeekArr.push(TriggerSummaryMessageInfo.DaysOfTheWeek[0]);
        }
        if (trigger.ScheduledMonday) {
            daysOfWeekArr.push(TriggerSummaryMessageInfo.DaysOfTheWeek[1]);
        }
        if (trigger.ScheduledTuesday) {
            daysOfWeekArr.push(TriggerSummaryMessageInfo.DaysOfTheWeek[2]);
        }
        if (trigger.ScheduledWednesday) {
            daysOfWeekArr.push(TriggerSummaryMessageInfo.DaysOfTheWeek[3]);
        }
        if (trigger.ScheduledThursday) {
            daysOfWeekArr.push(TriggerSummaryMessageInfo.DaysOfTheWeek[4]);
        }
        if (trigger.ScheduledFriday) {
            daysOfWeekArr.push(TriggerSummaryMessageInfo.DaysOfTheWeek[5]);
        }
        if (trigger.ScheduledSaturday) {
            daysOfWeekArr.push(TriggerSummaryMessageInfo.DaysOfTheWeek[6]);
        }
        const daysOfWeek = daysOfWeekArr.join(TriggerSummaryMessageInfo.DayDelimiter);
        return {
            time,
            dayOfMonth,
            daysOfWeek,
            dayOfYear
        };
    }
    const triggerSummaryKeysByType = {};
    Object.keys(TRIGGER_ACTION_TYPE).forEach((actionName) => {
        triggerSummaryKeysByType[TRIGGER_ACTION_TYPE[actionName]] = `TriggerSummary_${actionName}`;
    });

    const UNKNOWN = $app.res({
        namespace: NAMESPACE,
        key: KEYS.unknownEntityMessage
    });

    function $res(key, values) {
        return $app.res({ key, values });
    }

    return {
        namespaced: true,
        state: ProgramData,
        getters: {
            [TRIGGER_SUMMARY_MESSAGE_INFO]() {
                return TriggerSummaryMessageInfo;
            },
            [GET_TRIGGER_SUMMARY](state, getters) {
                return function (trigger) {
                    const status = getters.statusesById[trigger.ContextId]?.Name ?? "";
                    const statusTo = getters.statusesById[trigger.TransitionStatusId]?.Name ?? "";
                    const formDescription = getters.formsById[trigger.ContextId]?.Name ?? "";
                    const { channelName, signalName } = getters.signalInfoById[trigger.ContextId] ?? {
                        channelName: "",
                        signalName: ""
                    };
                    const { time, dayOfMonth, daysOfWeek, dayOfYear } = getTimeArgumentsFromTrigger(trigger);

                    const envelopeName = getters.envelopesById[trigger.ContextId]?.Name ?? "";

                    const values = [
                        status,
                        statusTo,
                        formDescription,
                        time,
                        dayOfMonth,
                        daysOfWeek,
                        dayOfYear,
                        signalName,
                        channelName,
                        envelopeName
                    ];
                    const key = triggerSummaryKeysByType[trigger.ActionType];

                    return $app.res({ key, values });
                };
            },
            [GET_TRIGGER_TYPE_NAME]() {
                return function (triggerOrType) {
                    let key;
                    let type = null;
                    if (typeof triggerOrType === "number") {
                        type = triggerOrType;
                    } else {
                        type = triggerOrType?.Type;
                    }
                    switch (type) {
                        case TRIGGER_TYPE.Form:
                            key = KEYS.Form;
                            break;
                        case TRIGGER_TYPE.Status:
                            key = KEYS.Status;
                            break;
                        case TRIGGER_TYPE.Scheduled:
                            key = KEYS.Scheduled;
                            break;
                        case TRIGGER_TYPE.Inquiry:
                            key = KEYS.Inquiry;
                            break;
                        case TRIGGER_TYPE.Channel:
                            key = KEYS.Channel;
                            break;
                        case TRIGGER_TYPE.ESignatureEnvelope:
                            key = KEYS.ESignatureEnvelope;
                            break;
                        default:
                            return "";
                    }
                    return $app.res({
                        namespace: NAMESPACE,
                        key
                    });
                };
            },
            [GET_ACTION_SUMMARY](state, getters) {
                return function (action) {
                    switch (action.Type) {
                        case ACTION_TYPE.ChangeStatus: {
                            const statusName = getters.statusesById[action.StatusId]?.Name ?? UNKNOWN;
                            return $res("ChangeProjectStatusActionMessage", [statusName]);
                        }
                        case ACTION_TYPE.SendCommunication: {
                            const templateName =
                                getters.communicationsById[action.CommunicationTemplateId]?.Name ?? UNKNOWN;
                            return $res("SendCommunicationActionMessage", [templateName]);
                        }
                        case ACTION_TYPE.SetDataField: {
                            const FIELD_MARKER = "f";
                            const fieldOrGroup = getters.dataFieldsById[action.DataId];
                            const targetName = fieldOrGroup?.Name ?? UNKNOWN;
                            const isField = action.DataId?.startsWith(FIELD_MARKER) ?? true;

                            // Copy data field
                            if (action.SourceDataId) {
                                const sourceName = getters.dataFieldsById[action.SourceDataId]?.Name ?? UNKNOWN;
                                if (isField) {
                                    return $res("CopyDataFieldActionMessage", [sourceName, targetName]);
                                } else {
                                    return $res("CopyGroupActionMessage", [sourceName, targetName]);
                                }
                            } else if (action.SourceFormulaId) {
                                const sourceFormulaName =
                                    getters[FORMULAS_BY_ID][action.SourceFormulaId]?.Name ?? UNKNOWN;
                                return $res("CopyFormulaActionMessage", [sourceFormulaName, targetName]);
                            } else if (fieldOrGroup) {
                                if (fieldOrGroup.TypeId === `${FIELD_MARKER}${DATA_FIELD_TYPE.Timestamp}`) {
                                    return $res("SetFieldToCurrentTimestampActionMessage", [targetName]);
                                }
                                if (fieldOrGroup.TypeId === `${FIELD_MARKER}${DATA_FIELD_TYPE.DateTime}`) {
                                    return $res("SetFieldToCurrentDateActionMessage", [targetName]);
                                }
                                if (action.AppendConsecutiveNumber) {
                                    return $res("SetFieldAppendNumberActionMessage", [
                                        targetName,
                                        action.DataFieldValue
                                    ]);
                                }
                                return $res("SetFieldActionMessage", [targetName, action.DataFieldValue]);
                            } else {
                                return $res("SetUnspecifiedFieldActionMessage");
                            }
                        }
                        case ACTION_TYPE.SetDeadline: {
                            const deadlineName = getters.deadlinesById[action.DeadlineId]?.Name ?? UNKNOWN;
                            return $res("SetDeadlineActionMessage", [deadlineName]);
                        }
                        case ACTION_TYPE.PauseDeadline: {
                            const deadlineName = getters.deadlinesById[action.DeadlineId]?.Name ?? UNKNOWN;
                            return $res("PauseDeadlineActionMessage", [deadlineName]);
                        }
                        case ACTION_TYPE.ResumeDeadline: {
                            const deadlineName = getters.deadlinesById[action.DeadlineId]?.Name ?? UNKNOWN;
                            return $res("ResumeDeadlineActionMessage", [deadlineName]);
                        }
                        case ACTION_TYPE.SatisfyDeadline:
                            if (action.SatisfyAllDeadlines) {
                                return $res("SatisfyAllDeadlinesActionMessage");
                            } else {
                                const deadlineName = getters.deadlinesById[action.DeadlineId]?.Name ?? UNKNOWN;
                                return $res("SatisfyDeadlineActionMessage", [deadlineName]);
                            }
                        case ACTION_TYPE.CallWebAdapter: {
                            const config = getters.adapterConfigurationsById[action.AdapterConfigurationId];
                            const configName = config?.ConfigurationName ?? UNKNOWN;
                            return $res("CallConnectionActionMessage", [configName]);
                        }
                        case ACTION_TYPE.ClearAssignee:
                            return $res("ClearAssigneeActionMessage");
                        case ACTION_TYPE.GenerateAttachment: {
                            const docTemplateName =
                                getters.documentTemplatesById[action.DocumentTemplateId]?.Name ?? UNKNOWN;
                            const attachmentName = getters.attachmentsById[action.AttachmentId]?.Name ?? UNKNOWN;
                            if (action.GenerateAsDocx === true) {
                                return $res("GenerateAttachmentAsWordDocActionMessage", [
                                    attachmentName,
                                    docTemplateName
                                ]);
                            }
                            if (action.GenerateAsDocx === false) {
                                return $res("GenerateAttachmentAsPdfActionMessage", [attachmentName, docTemplateName]);
                            }
                            return $res("GenerateAttachmentActionMessage", [attachmentName, docTemplateName]);
                        }
                        case ACTION_TYPE.SignalToChannel:
                            if (action.ChannelRuleId) {
                                const { channelName, signalName } = getters.signalInfoById[action.ChannelRuleId] ?? {
                                    channelName: UNKNOWN,
                                    signalName: UNKNOWN
                                };
                                return $res("ConfiguredSignalActionMessage", [signalName, channelName]);
                            } else {
                                return $res("UnconfiguredSignalActionMessage");
                            }
                        case ACTION_TYPE.AddAccessGroup: {
                            const accessGroupName = getters.accessGroupsById[action.AccessGroupId]?.Name ?? UNKNOWN;
                            return $res("AddAccessGroupAutomationMessage", [accessGroupName]);
                        }
                        case ACTION_TYPE.RemoveAccessGroup:
                            if (action.RemoveAllAccessGroups) {
                                return $res("RemoveAllAccessGroupsAutomationMessage");
                            } else {
                                const accessGroupName = getters.accessGroupsById[action.AccessGroupId]?.Name ?? UNKNOWN;
                                return $res("RemoveAccessGroupAutomationMessage", [accessGroupName]);
                            }
                        case ACTION_TYPE.RunReport: {
                            const reportName = getters.reportsById[action.ReportId]?.Name ?? UNKNOWN;
                            return $res("RunReportActionMessage", [reportName]);
                        }
                        case ACTION_TYPE.RequestESignature: {
                            const envelopeName = getters.envelopesById[action.EnvelopeDefinitionId]?.Name ?? UNKNOWN;
                            return $res("RequestESignatureMessage", [envelopeName]);
                        }
                        case ACTION_TYPE.ClearDataField: {
                            const field = getters.dataFieldsById[action.DataId];
                            const targetName = field?.Name ?? UNKNOWN;
                            if (field) {
                                return $res("ClearFieldActionMessage", [targetName]);
                            } else {
                                return $res("ClearUnspecifiedFieldActionMessage");
                            }
                        }
                        default:
                            return $app.res({
                                namespace: NAMESPACE,
                                key: KEYS.unknownActionMessage
                            });
                    }
                };
            },
            envelopesById(state) {
                return collectionById(state.ESignatureEnvelopes);
            },
            [DATA_FIELDS_BY_ID](state) {
                return collectionById(state.DataFields);
            },
            statusesById(state) {
                return collectionById(state.Statuses);
            },
            formsById(state) {
                return collectionById(state.Forms);
            },
            [FORMULAS_BY_ID](state) {
                return collectionById(state.Formulas);
            },
            communicationsById(state) {
                return collectionById(state.CommunicationTemplates);
            },
            deadlinesById(state) {
                return collectionById(state.Deadlines);
            },
            adapterConfigurationsById(state) {
                return collectionById(state.AdapterConfigurations);
            },
            documentTemplatesById(state) {
                return collectionById(state.DocumentTemplates);
            },
            attachmentsById(state) {
                return collectionById(state.Attachments);
            },
            accessGroupsById(state) {
                return collectionById(state.AccessGroups);
            },
            reportsById(state) {
                return collectionById(state.Reports);
            },
            signalInfoById(state) {
                const byId = {};
                state.Channels.forEach((channel) =>
                    channel.Signals.forEach((signal) => {
                        byId[signal.PublicId] = {
                            channelName: channel.Name,
                            signalName: signal.StaticValue
                        };
                    })
                );
                return byId;
            },
            [GET_FORMULAS_BY_RESULT_TYPE](state) {
                return function (resultType) {
                    return state.Formulas.filter((f) => f.ResultTypeId === resultType);
                };
            }
        }
    };
}
